/**
 * Matching a roster size label ("Men XL") to a group in the masterfile's
 * sizes SVG.
 *
 * An XML/SVG `id` cannot contain a space, so design tools rewrite the layer
 * name on export. Adobe Illustrator encodes every character that is invalid
 * in an XML name as `_xHH_` — "Men XL" becomes `Men_x20_XL`, "Anchor_1"
 * becomes `Anchor_x5F_1` — and other tools substitute an underscore or drop
 * the space. An exact string comparison therefore misses the group the
 * designer clearly intended, and the row is reported as "size not found"
 * even though the masterfile has it.
 *
 * So the lookup compares NORMALIZED forms: Illustrator escapes decoded,
 * Illustrator's numeric export suffix removed, separators folded, case and
 * surrounding whitespace ignored. The exact id always wins; the looser
 * passes only decide between the remaining candidates.
 */

/** Decode Illustrator's `_xHH_` / `_xHHHH_` id escapes back to characters. */
export function decodeSvgIdEscapes(id: string): string {
  return id.replace(/_x([0-9A-Fa-f]{2,6})_/g, (_m, hex: string) => {
    const code = parseInt(hex, 16);
    return Number.isFinite(code) && code > 0 ? String.fromCodePoint(code) : _m;
  });
}

/**
 * Illustrator appends a long numeric uniqueness suffix on export
 * (`FrontPanel_00000183963186077782573190000010581738469143546265_`).
 * It carries no meaning for matching.
 */
export function stripSvgIdSuffix(id: string): string {
  return id.replace(/_0{2,}\d*_?$/, '').replace(/_+$/, '');
}

/**
 * Comparison key for a size name or a size group id: escapes decoded, export
 * suffix stripped, non-breaking spaces normalized, separators (space, _, -)
 * collapsed to one space, trimmed, lowercased.
 */
export function normalizeSizeKey(value: string): string {
  return stripSvgIdSuffix(decodeSvgIdEscapes(String(value ?? '')))
    .replace(/\u00a0/g, ' ')
    .replace(/[\s_-]+/g, ' ')
    .trim()
    .toLowerCase();
}

/** The same key with every separator removed ("men xl" -> "menxl"). */
export function compactSizeKey(value: string): string {
  return normalizeSizeKey(value).replace(/ /g, '');
}

export interface SizeMatch {
  /** The id as it appears in the document — use this to query the group. */
  id: string;
  /** True when the roster label equalled the id verbatim. */
  exact: boolean;
}

/**
 * Resolve a roster size label to one of the sizes SVG's group ids.
 * Passes, in order: verbatim, decoded, normalized, separator-free. Returns
 * null when nothing plausibly matches, and never guesses between two
 * candidates that tie on a looser pass (the first document-order one wins,
 * which mirrors how the designer stacked them).
 */
export function resolveSizeGroupId(candidateIds: string[], wanted: string): SizeMatch | null {
  const ids = candidateIds.filter((id) => !!id);
  if (!ids.length || !String(wanted ?? '').trim()) return null;

  const exact = ids.find((id) => id === wanted);
  if (exact) return { id: exact, exact: true };

  const decoded = ids.find((id) => decodeSvgIdEscapes(id) === wanted);
  if (decoded) return { id: decoded, exact: false };

  const wantedKey = normalizeSizeKey(wanted);
  const normalized = ids.find((id) => normalizeSizeKey(id) === wantedKey);
  if (normalized) return { id: normalized, exact: false };

  const wantedCompact = compactSizeKey(wanted);
  const compact = ids.find((id) => compactSizeKey(id) === wantedCompact);
  if (compact) return { id: compact, exact: false };

  return null;
}

/** Group ids of a parsed sizes SVG, in document order. */
export function sizeGroupIds(svgDoc: Document): string[] {
  return Array.from(svgDoc.querySelectorAll('g[id]'))
    .map((g) => g.getAttribute('id') ?? '')
    .filter((id) => !!id);
}

/** The family part of a size id: "Men_x20_4XL" -> "men", "Youth_L" -> "youth", "XL" -> "". */
export function sizeFamilyOf(id: string): string {
  const parts = normalizeSizeKey(id).split(' ');
  return parts.length > 1 ? parts.slice(0, -1).join(' ') : '';
}

/**
 * Every group that could be the base size named by `baseToken`: the direct
 * match when there is one, else the groups whose LAST token is the base
 * ("Men_L" and "Youth_L" for "L"), in document order.
 */
export function baseSizeCandidates(candidateIds: string[], baseToken: string): string[] {
  const direct = resolveSizeGroupId(candidateIds, baseToken);
  if (direct) return [direct.id];
  const baseKey = normalizeSizeKey(baseToken);
  if (!baseKey) return [];
  return candidateIds.filter((id) => {
    const parts = normalizeSizeKey(id).split(' ');
    return parts.length > 1 && parts[parts.length - 1] === baseKey;
  });
}

/**
 * Resolve the GRADING BASE group when the sizes SVG carries no Anchor to mark
 * it. `baseToken` is the size the production atlas is drawn at — the
 * masterfile's wrapper group (e.g. "L") or the platform default — and
 * `targetId` the already-resolved roster size group. A multi-family sizes
 * file ("Men_L", "Youth_L") shares the base token across families, so the
 * candidate in the target's family wins; otherwise the first in document
 * order. Null when no group ends in the base token at all.
 */
export function resolveBaseSizeGroupId(
  candidateIds: string[],
  baseToken: string,
  targetId?: string | null
): SizeMatch | null {
  const direct = resolveSizeGroupId(candidateIds, baseToken);
  if (direct) return direct;

  const endsWithBase = baseSizeCandidates(candidateIds, baseToken);
  if (!endsWithBase.length) return null;

  if (targetId) {
    const family = sizeFamilyOf(targetId);
    const sameFamily = endsWithBase.find((id) => sizeFamilyOf(id) === family);
    if (sameFamily) return { id: sameFamily, exact: false };
  }
  return { id: endsWithBase[0], exact: false };
}

export interface ContentRootResolution {
  root: Element;
  wrappers: string[];
  /**
   * Elements found BESIDE a wrapper that was descended into: artwork the
   * designer drew straight into the exported template, outside the atlas
   * group. Not panels — the caller files them under the panel they lie on.
   */
  loose: Element[];
}

const PANEL_OR_ANCHOR_ID = /panel|anchor/i;

/** A group is the atlas wrapper when it holds panel groups (or the Anchor) and is not a panel itself. */
function holdsPanels(g: Element): boolean {
  if (PANEL_OR_ANCHOR_ID.test(g.getAttribute('id') ?? '')) return false;
  return Array.from(g.querySelectorAll('[id]')).some((el) =>
    PANEL_OR_ANCHOR_ID.test(el.getAttribute('id') ?? '')
  );
}

/**
 * Descend through wrapper groups to the element whose children are the real
 * top-level groups. Size-wrapped masters nest the whole production atlas in
 * one wrapper (Production > L > panels…), and that wrapper's id is the size
 * the atlas is drawn at. `wrappers` lists the ids passed (outermost first,
 * '' for an unnamed group).
 *
 * A single `<g>` child is always descended. A level with SEVERAL children is
 * descended too when exactly one of them is the atlas wrapper (holds the
 * panel groups / Anchor) and none of the others is panel-like: that is what a
 * customer's custom design looks like when they add shapes to the exported
 * template in Illustrator — the shapes land next to the `L` group, not inside
 * a panel. Those siblings are returned as `loose`; treating them as
 * top-level groups made every one of them a "panel" with its own laser
 * outline, and folded the whole atlas into one piece named "l".
 */
export function descendSingleGroupWrappers(root: Element): ContentRootResolution {
  const skip = new Set(['defs', 'title', 'desc', 'metadata', 'style']);
  const wrappers: string[] = [];
  const loose: Element[] = [];
  let node = root;
  for (;;) {
    const kids = Array.from(node.children).filter((c) => !skip.has(c.tagName.toLowerCase()));
    const groups = kids.filter((c) => c.tagName.toLowerCase() === 'g');
    let next: Element | null = null;
    if (kids.length === 1 && groups.length === 1) {
      next = groups[0];
    } else if (kids.length > 1) {
      const holders = groups.filter(holdsPanels);
      const others = kids.filter((k) => k !== holders[0]);
      if (
        holders.length === 1 &&
        !others.some((k) => PANEL_OR_ANCHOR_ID.test(k.getAttribute('id') ?? ''))
      ) {
        next = holders[0];
        loose.push(...others);
      }
    }
    if (!next) return { root: node, wrappers, loose };
    node = next;
    wrappers.push(node.getAttribute('id') ?? '');
  }
}
